// ============================================================
// THE PSYKER, DRAWN (plan body-map T1; engine/plate/psyker). Three layers:
// behind the figure (the pillar, lifted shards, the well's grit, the back
// half of the ripples and embers), before it (the lit body and its motes,
// the near embers, the ash, the ripples' front half) - both inside the
// figure's idle group, so they move with it - and on the plate over it (the
// ring, its light, the runes, the arc, the crown), as the bench drew it.
// The particles loop on tracks; the runes, crown, arc, ash and shards are
// the scene the plate's clock writes each frame (`psykerNodes`). Drawn, it
// is that scene at a still moment, the particles unseen.
// ============================================================

import { useMemo, type ReactNode } from 'react';
import type { Box } from '../../../../engine/plate/frame';
import {
  arcAt,
  ash,
  ashAt,
  crownAt,
  debris,
  embers,
  grit,
  motes,
  NO_ARC,
  psyFloor,
  psyLight,
  ripples,
  runeAt,
  runeCount,
  shardAt,
  type PsyFloor,
} from '../../../../engine/plate/psyker';
import clsx from '../../../../utils/clsx';
import tx from '../../../../styles/PlateTraits.module.css';
import { tracked } from '../../usePlateClock';
import type { Attrs, WrittenNode } from '../scene';
import { glyph } from './voynich';

/** The moment the still look shows: the runes spread up the ring, no arc. */
const STILL_T = 1.7;

/** A rune's three stroked layers, back to front: as it turns, the back two slide out along its edge. */
const SLABS = [0.3, 0.55, 1];
const CROWN = 10;

interface Props {
  u: string;
  /** Psy Rating, 1-10. */
  r: number;
  /** The figure's whole bounds. */
  box: Box;
}

/** A DOM attribute's name as React takes it (`stroke-dasharray` -> `strokeDasharray`). */
const reactName = (name: string) => name.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());

/** The still scene by key, its attributes named for React: what each scene element is drawn with. */
type Still = Map<string, Attrs>;
function useStill(r: number, box: Box): Still {
  return useMemo(
    () =>
      new Map(
        psykerStill(r, box).map((n) => [
          n.key,
          Object.fromEntries(Object.entries(n.attrs).map(([k, v]) => [reactName(k), v])),
        ]),
      ),
    [r, box],
  );
}

/** The psyker's own glow and gradients (the looks' shared soft glow, `<u>-tglow`, is TraitLayers'). */
export function PsykerDefs({ u, r }: { u: string; r: number }) {
  return (
    <>
      <filter id={`${u}-pglow`} x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation={9} result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <radialGradient id={`${u}-pg`}>
        <stop offset={0} className={tx.psyLightStop} stopOpacity={0.55} />
        <stop offset={1} className={tx.psyLightStop} stopOpacity={0} />
      </radialGradient>
      <linearGradient id={`${u}-pc`} x1={0} y1={1} x2={0} y2={0}>
        <stop offset={0} className={tx.psyLightStop} stopOpacity={0.5} />
        <stop offset={1} className={tx.psyLightStop} stopOpacity={0} />
      </linearGradient>
      {r >= 10 && (
        <linearGradient id={`${u}-pil`}>
          <stop offset={0} className={tx.psyBodyStop} stopOpacity={0} />
          <stop offset={0.5} className={tx.psyBodyStop} stopOpacity={0.9} />
          <stop offset={1} className={tx.psyBodyStop} stopOpacity={0} />
        </linearGradient>
      )}
    </>
  );
}

/** At 10: a pillar of pale light from the ring to the top of the plate, flickering, a brighter core inside it. */
function Pillar({ u, f, top }: { u: string; f: PsyFloor; top: number }) {
  const w = f.rx * 2.1;
  const at = { y: top, height: f.gy + 10 - top, fill: `url(#${u}-pil)` };
  return (
    <>
      <rect
        x={f.cx - w / 2}
        width={w}
        {...at}
        {...tracked([{ attr: 'opacity', values: [0.22, 0.34, 0.25, 0.3, 0.22], dur: 2.3 }])}
      />
      <rect
        x={f.cx - w * 0.12}
        width={w * 0.24}
        {...at}
        {...tracked([{ attr: 'opacity', values: [0.08, 0.16, 0.08], dur: 1.7 }])}
      />
    </>
  );
}

/** Embers rising off the ring: the near half before the figure, the far half behind it. */
function Embers({ u, f, r, front }: { u: string; f: PsyFloor; r: number; front: boolean }) {
  return embers(f, r)
    .filter((e) => e.front === front)
    .map(({ r: size, front: _, ...tracks }, k) => (
      <circle
        key={k}
        className={tx.psyEmber}
        r={size}
        filter={`url(#${u}-tglow)`}
        {...tracked(Object.values(tracks))}
      />
    ));
}

/** The shockwave ripples: the far half of each ring behind the figure, the near half (clipped) before it. */
function Ripples({ u, f, r, front }: { u: string; f: PsyFloor; r: number; front: boolean }) {
  return (
    <g clipPath={front ? `url(#${u}-pfront)` : undefined}>
      {front && (
        <clipPath id={`${u}-pfront`}>
          <rect x={f.cx - 4000} y={f.gy} width={8000} height={3000} />
        </clipPath>
      )}
      {ripples(f, r).map((tracks, k) => (
        <ellipse key={k} className={tx.psyRipple} cx={f.cx} cy={f.gy} {...tracked(Object.values(tracks))} />
      ))}
    </g>
  );
}

/** Behind the figure: the pillar (10), lifted shards (8+), the well's grit, the far ripples and embers. */
export function PsykerBack({ u, r, box }: Props) {
  const f = psyFloor(box);
  const still = useStill(r, box);
  return (
    <g pointerEvents="none" data-trait="psyker">
      {r >= 10 && <Pillar u={u} f={f} top={-560} />}
      {debris(f, r).map((d, k) => (
        <g key={k} data-g={`psy-d${k}`} {...still.get(`psy-d${k}`)}>
          <polygon
            className={tx.psyShard}
            points={`0,${-d.size} ${d.size * 0.8},${d.size * 0.4} ${-d.size * 0.6},${d.size}`}
          />
        </g>
      ))}
      {grit(f, r).map(({ r: size, ...tracks }, k) => (
        <circle key={k} className={tx.psyGrit} r={size} {...tracked(Object.values(tracks))} />
      ))}
      <Ripples u={u} f={f} r={r} front={false} />
      <Embers u={u} f={f} r={r} front={false} />
    </g>
  );
}

/** Before the figure: the lit body (3+) and its motes, the near embers, the ash, the near ripples. */
export function PsykerFront({ u, r, box }: Props) {
  const f = psyFloor(box);
  const still = useStill(r, box);
  return (
    <g pointerEvents="none">
      {r >= 3 && (
        <g className={tx.psyBody} opacity={0.18 + 0.05 * r} filter={`url(#${u}-tglow)`}>
          <use href={`#${u}-art`} fill="currentColor" />
        </g>
      )}
      {motes(box, r).map(({ cx, ...tracks }, k) => (
        <circle
          key={k}
          className={tx.psyMote}
          cx={cx}
          r={9}
          filter={`url(#${u}-tglow)`}
          {...tracked(Object.values(tracks))}
        />
      ))}
      <Embers u={u} f={f} r={r} front />
      {ash(f, r).map((_, k) => (
        <rect
          key={k}
          className={tx.psyAsh}
          data-g={`psy-a${k}`}
          x={-11}
          y={-4}
          width={22}
          height={8}
          filter={`url(#${u}-tglow)`}
          {...still.get(`psy-a${k}`)}
        />
      ))}
      <Ripples u={u} f={f} r={r} front />
    </g>
  );
}

/** One rune: three stroked layers of its glyph, each placed by the clock. */
function Rune({ k, still }: { k: number; still: Still }) {
  return (
    <g data-g={`psy-r${k}`} {...still.get(`psy-r${k}`)}>
      {SLABS.map((o, j) => (
        <g key={j} data-g={`psy-r${k}s${j}`} opacity={o} {...still.get(`psy-r${k}s${j}`)}>
          <path d={glyph(k)} transform="scale(2.8) translate(-50 -55)" strokeWidth={5.5} />
        </g>
      ))}
    </g>
  );
}

/** On the plate over the figure: the column of light, the glow, the ring, the runes, the arc, the crown (9+). */
export function PsykerRing({ u, r, box, gutter = false }: Props & { gutter?: boolean }): ReactNode {
  const still = useStill(r, box);
  return (
    <g
      className={clsx(tx.psy, gutter && tx.psyGutter)}
      pointerEvents="none"
      data-testid="plate-psyker"
      data-psy={r}
      data-gutter={gutter || undefined}
    >
      <rect className={tx.psyColumn} data-g="psy-col" fill={`url(#${u}-pc)`} {...still.get('psy-col')} />
      <ellipse className={tx.psyGlow} data-g="psy-glow" fill={`url(#${u}-pg)`} {...still.get('psy-glow')} />
      <ellipse className={tx.psyRing} data-g="psy-ring" {...still.get('psy-ring')} />
      <g filter={`url(#${u}-pglow)`}>
        {Array.from({ length: runeCount(r) }, (_, k) => (
          <Rune key={k} k={k} still={still} />
        ))}
      </g>
      <path className={tx.psyArc} data-g="psy-arc" {...still.get('psy-arc')} />
      {r >= 9 && (
        <g filter={`url(#${u}-pglow)`}>
          {Array.from({ length: CROWN }, (_, k) => (
            <g key={k} data-g={`psy-crown${k}`} {...still.get(`psy-crown${k}`)}>
              <path d={glyph(k + 3)} transform="scale(2.8) translate(-50 -55)" strokeWidth={5.5} />
            </g>
          ))}
        </g>
      )}
    </g>
  );
}

/** The runes at `t`: each rune's brightness and broken strokes, and its slabs' places. */
function runeNodes(f: PsyFloor, r: number, t: number) {
  const runes = Array.from({ length: runeCount(r) }, (_, k) => runeAt(k, f, r, t));
  const nodes = runes.flatMap((q, k): WrittenNode[] => [
    { key: `psy-r${k}`, attrs: { opacity: q.opacity, 'stroke-dasharray': q.dash } },
    ...q.slabs.map((transform, j) => ({ key: `psy-r${k}s${j}`, attrs: { transform } })),
  ]);
  return { runes, nodes };
}

/**
 * What the clock writes of the psyker at `t`: the ring's light, the runes, the arc, the crown, the ash and the shards.
 * `still`: the still moment - no arc, the ash and shards unseen.
 */
export function psykerNodes(r: number, box: Box, t: number, still = false): WrittenNode[] {
  const f = psyFloor(box);
  const light = psyLight(f, r, t);
  const { runes, nodes } = runeNodes(f, r, t);
  const hidden = still ? { opacity: 0 } : {};
  return [
    { key: 'psy-ring', attrs: light.ring },
    { key: 'psy-glow', attrs: light.glow },
    { key: 'psy-col', attrs: light.column },
    ...nodes,
    { key: 'psy-arc', attrs: still ? NO_ARC : arcAt(t, r, runes) },
    ...(r >= 9 ? Array.from({ length: CROWN }, (_, k) => ({ key: `psy-crown${k}`, attrs: crownAt(k, box, t) })) : []),
    ...ash(f, r).map((d, k) => ({ key: `psy-a${k}`, attrs: { ...ashAt(d, t), ...hidden } })),
    ...debris(f, r).map((d, k) => ({ key: `psy-d${k}`, attrs: { ...shardAt(d, t), ...hidden } })),
  ];
}

/** The psyker's still look: its scene at the still moment, as drawn before the clock (or once it stops). */
export const psykerStill = (r: number, box: Box) => psykerNodes(r, box, STILL_T, true);
